export type Currency = 'USD' | 'EUR';
export type TimeWindow = '1D' | '7D' | '30D' | '90D' | '1Y' | '2Y';
export type Theme = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

export type Provider = 'yahoo' | 'manual';

export type PositionLeg = {
  broker?: string | null;
  quantity: number;
  avg_price?: number | null;
};

export type RawHolding = {
  symbol: string;
  name: string;
  bucket_id: string;
  provider: Provider;
  provider_symbol: string | null;
  quantity?: number | null;
  avg_price?: number | null;
  risk: number;
  index?: string;
  merge_group?: string;
  geo?: string;
  ccy?: string;
  asset_class?: 'equity' | 'crypto_volatile' | 'bond' | 'cash_eq' | 'gold';
  /** Persisted per-broker legs when present; omitted when none materialized yet. */
  positions?: PositionLeg[];
};

export type RawBucket = {
  id: string;
  name: string;
  color_accent: string;
  holdings: RawHolding[];
};

export type NonTradableItem = {
  id: string;
  name: string;
  value_eur: number | null;
  value_usd: number | null; // derived at request time from value_eur * live eur_usd
  liquidity: 'low' | 'very_low' | 'illiquid';
};

export type RawNonTradableItem = Omit<NonTradableItem, 'value_usd' | 'value_eur'> & {
  value_eur?: number | null;
};

export type RawPortfolio = {
  buckets: RawBucket[];
  non_tradable: RawNonTradableItem[];
};

export type Delta = {
  abs_usd: number;
  abs_eur: number;
  pct: number;
};

export type Deltas = {
  d1: Delta;
  d7: Delta;
  d30: Delta;
  d90: Delta;
  ytd: Delta;
  y1: Delta;
  y2: Delta;
};

export const DELTA_KEYS = ['d1', 'd7', 'd30', 'd90', 'ytd', 'y1', 'y2'] as const satisfies readonly (keyof Deltas)[];

export type PnL = {
  cost_basis_usd: number;
  abs_usd: number;
  abs_eur: number;
  pct: number;
  coverage: number;
};

export type HoldingRow = {
  symbol: string;
  name: string;
  bucket_id: string;
  provider?: Provider;
  provider_symbol?: string | null;
  asset_class?: RawHolding['asset_class'];
  risk: number;
  quantity: number;
  avg_price: number | null;
  ccy: string;
  price_usd: number | null;
  value_usd: number | null;
  value_eur: number | null;
  share_in_bucket: number;
  share_in_portfolio: number;
  deltas: Deltas;
  pnl: PnL | null;
  warning?: string;
  // Underlying-index key used by the "merge same index" toggle (resolved
  // merge_group ?? index). Present only on tradable rows that carry one.
  merge_group?: string;
  // Constituent ticker symbols when this row is a merged index group.
  members?: string[];
  /** Per-broker legs when materialized; edit UI seeds from qty/avg if absent. */
  positions?: PositionLeg[];
};

export type Bucket = {
  id: string;
  name: string;
  color_accent: string;
  share: number;
  value_usd: number;
  value_eur: number;
  weighted_risk: number;
  deltas: Deltas;
  pnl: PnL | null;
  holdings: HoldingRow[];
};

export type AssetClassRollup = { class: string; value_usd: number; value_eur: number; share: number };
export type GeographyRollup = { region: string; value_usd: number; value_eur: number; share: number };
export type CurrencyExposure = { ccy: string; value_usd: number; share: number };
export type IndexExposure = { index: string; tickers: string[]; value_usd: number; value_eur: number; share: number };
export type RiskHeatmapPoint = { symbol: string; bucket: string; value_usd: number; risk: number };

export type PortfolioResponse = {
  snapshot_at: string;
  prices_updated_at: string | null;
  fx: { eur_usd: number; updated_at: string };
  totals: {
    tradable_usd: number;
    tradable_eur: number;
    non_tradable_usd: number;
    non_tradable_eur: number;
    grand_total_usd: number;
    grand_total_eur: number;
    deltas: Deltas;
    pnl: PnL;
    weighted_risk: number;
  };
  buckets: Bucket[];
  asset_classes: AssetClassRollup[];
  geography: GeographyRollup[];
  currency_exposure: CurrencyExposure[];
  index_consolidation: IndexExposure[];
  top_holdings: HoldingRow[];
  risk_heatmap: RiskHeatmapPoint[];
  non_tradable: NonTradableItem[];
  warnings: string[];
};
